import { Modal, WizardView } from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Route, Switch, useHistory, useParams, generatePath } from 'react-router-dom';
import { OffsiteCampaignEndpoints } from 'sections/offsite-campaigns/constants/api-endpoints';
import { IOffsiteCampaign } from 'sections/offsite-campaigns/entities/offsiteCampaign';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import { useNewOffsiteCampaignDispatch, useNewOffsiteCampaignState } from '../context/UseNewOffsiteCampaignContext';
import { getSteps, stepRoutes } from '../data/NewCampaignConstants';
import { arePreviousStepsValid, buildNewCampaignsPath, redirectToLastValidStep } from '../utils/helpers';
import { DetailsStep } from './steps/details-page/DetailsStep';
import { FeaturesStep } from './steps/features-page/FeaturesStep';

export const NewOffsiteCampaignWizard: React.FC = () => {
  const { t } = useTranslation('campaigns');
  const { push } = useHistory();
  const { siteId, campaignWizardPage } = useParams<{ siteId: string; campaignWizardPage: string }>();
  const { validSteps, campaign } = useNewOffsiteCampaignState();
  const steps = getSteps(t);
  const dispatch = useNewOffsiteCampaignDispatch();
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const [modalVisible, setModalVisible] = React.useState(false);

  /**
   * @deprecated use useCreateOffsiteCampaignMutation instead.
   */
  const saveOffsiteCampaignMutation = useMutation((campaign: IOffsiteCampaign) =>
    RestApi.post<{ id: string }, IOffsiteCampaign>(
      generatePath(OffsiteCampaignEndpoints.OFFSITE_POST, { siteId }),
      campaign,
    ),
  );

  const handleSaveCampaign = async () => {
    campaign.siteId = siteId;
    campaign.version = 'v2';
    saveOffsiteCampaignMutation.mutate(campaign, {
      onSuccess: data => {
        push(generatePath(OffsiteCampaignRoute.OFFSITE_EDIT, { siteId, campaignId: data.data.id }), {
          showAddNewSegmentModal: false,
          addDefaultSegment: true,
        });
      },
      onError: (error: RestApiError, variables, context) => {
        dispatch({ type: 'CLEAR_ERROR' });
        dispatch({
          type: 'CREATE_CAMPAIGN_FAILED',
          payload: { createdCombinedCampaign: null, success: false, error: true, errorMessage: error.message },
        });
      },
    });
  };

  useEffect(() => () => dispatch({ type: 'RESET_WIZARD' }), [dispatch]);

  useEffect(() => {
    setCurrentStepIndex(stepRoutes.indexOf(`/${campaignWizardPage}`) + 1);
  }, [campaignWizardPage]);

  useEffect(() => {
    const prevStepsValid = arePreviousStepsValid(validSteps, currentStepIndex);

    if (!prevStepsValid) {
      redirectToLastValidStep(steps, validSteps, push);
    }
  }, [currentStepIndex, push, steps, validSteps]);

  const onPrevButtonClicked = () => {
    if (currentStepIndex >= 2) {
      push(buildNewCampaignsPath(siteId, steps[currentStepIndex - 2].route));
    } else {
      push(generatePath(OffsiteCampaignRoute.OFFSITE_LIST, { siteId }));
    }
  };

  const onNextButtonClicked = () => {
    if (currentStepIndex !== steps.length) {
      push(buildNewCampaignsPath(siteId, steps[currentStepIndex].route));
    } else {
      setModalVisible(true);
    }
  };

  const isValidStep = () => {
    const stepValid = !!validSteps.find(x => x === currentStepIndex);
    return stepValid;
  };

  function getName(campaign: IOffsiteCampaign): string {
    return campaign?.name || '';
  }

  return (
    <>
      <WizardView
        currentStep={currentStepIndex}
        steps={steps.map(step => {
          return { label: step.label };
        })}
        onPrevButtonClicked={onPrevButtonClicked}
        onNextButtonClicked={onNextButtonClicked}
        nextButtonDisabled={!isValidStep()}
      >
        <Switch>
          <Route
            exact={true}
            path={`/sites/:siteId/campaigns/offsite/newcampaign${steps[0].route}`}
            render={() => currentStepIndex === 1 && <FeaturesStep currentStep={currentStepIndex} />}
          />
          <Route
            exact={true}
            path={`/sites/:siteId/campaigns/offsite/newcampaign${steps[1].route}`}
            render={() => currentStepIndex === 2 && <DetailsStep currentStep={currentStepIndex} />}
          />
        </Switch>
      </WizardView>
      <Modal
        variation="clean"
        isOpen={modalVisible}
        heading={t('newCampaigns.modal.heading')}
        okButtonLabel={t('newCampaigns.modal.okButton.label')}
        onCancelButtonClicked={() => setModalVisible(false)}
        onOkButtonClicked={handleSaveCampaign}
      >
        <p>
          {t('newCampaigns.modal.messages.confirmationMessage')} {getName(campaign)}
        </p>
        <p>{t('newCampaigns.modal.messages.confirmationCancelOrCreate')}</p>
      </Modal>
    </>
  );
};

export default NewOffsiteCampaignWizard;
